import { DateRange } from '@mui/x-date-pickers-pro';
import { DateRangeCardProps } from './props';
import { DateRangePicker } from '@starsoft/common/components';
import { startOfDay, endOfDay } from 'date-fns';
import styles from './styles.module.scss';
import DateGroup from './DateGroup';
import { useMemo } from 'react';

/**
 * Component that renders a date range selection card with a date group and date range picker
 * Handles date range selection and formatting of start/end dates
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.isLoading] - Whether the component is in loading state
 * @param {string} [props.label] - Label text for the date range picker
 * @param {boolean} [props.isDefault] - Whether this is the default date range
 * @param {boolean} [props.isSmall] - Whether to render in small size
 * @param {DateRange<Date>} props.range - Currently selected date range
 * @param {Function} props.setRange - Callback to update the selected date range
 * @returns {JSX.Element} Date range card with date group and picker components
 */
export function DateRangeCard({
  isLoading,
  label,
  isDefault,
  isSmall,
  range,
  setRange,
}: DateRangeCardProps) {
  const [startDate, endDate] = range;
  const dateRange: DateRange<Date> = useMemo(
    () => [startDate, endDate],
    [startDate, endDate],
  );

  /**
   * Handles changes to the selected date range
   * Formats start date to start of day and end date to end of day
   *
   * @param {DateRange<Date>} range - New date range selected
   */
  function handleDateChange(range: DateRange<Date>) {
    const [rawStartDate, rawEndDate] = range;
    const startDate = rawStartDate ? startOfDay(rawStartDate) : null;
    const endDate = rawEndDate ? endOfDay(rawEndDate) : null;

    setRange(startDate, endDate);
  }

  return (
    <div className={styles.container}>
      <DateGroup onChange={handleDateChange} isDefault={isDefault} />
      <DateRangePicker
        range={dateRange}
        setRange={handleDateChange}
        isLoading={isLoading}
        label={label}
        isDefault={isDefault}
        isSmall={isSmall}
      />
    </div>
  );
}
